import React from 'react';
import { Box, SimpleGrid, Text, VStack, Heading } from '@chakra-ui/react';
import { Link, graphql } from 'gatsby';
import { GatsbyImage, getImage } from 'gatsby-plugin-image';
import styled from 'styled-components';
import Layout from '../components/layout';
import Footer from '../components/common/Footer';
import Seo from '../components/seo';
import { useTranslation } from 'react-i18next';

const PageContainer = styled(Box)`
  padding: 2rem 0;
  min-height: 100vh;
  background: linear-gradient(135deg, #1a1a2e 0%, #16213e 100%);
  display: flex;
  flex-direction: column;
  width: 100%;
`;

const ContentContainer = styled(Box)`
  flex: 1;
  max-width: 1200px;
  margin: 0 auto;
  padding: 2rem;
  width: 100%;
`;

const PageTitle = styled(Text)`
  color: white;
  font-size: 2.5rem;
  font-weight: bold;
  text-align: center;
  margin-bottom: 1rem;
`;

const Slogan = styled(Text).attrs({ as: 'h1' })`
  color: rgba(255, 255, 255, 0.9);
  font-size: 1.2rem;
  text-align: center;
  font-style: italic;
  margin-bottom: 1.5rem;
`;

const Description = styled(Text)`
  color: white;
  font-size: 1.4rem;
  text-align: center;
  max-width: 800px;
  margin: 0 auto 3rem;
  line-height: 1.6;
`;

const SectionTitle = styled(Heading)`
  color: white;
  font-size: 1.8rem;
  margin-top: 2.5rem;
  margin-bottom: 1.5rem;
  border-bottom: 2px solid rgba(147, 112, 219, 0.5);
  padding-bottom: 0.5rem;
`;

const InfoCard = styled(Link)`
  position: relative;
  height: 300px;
  border-radius: 12px;
  overflow: hidden;
  transition: all 0.3s ease;
  border: 2px solid rgba(147, 112, 219, 0.3);

  &:hover {
    transform: translateY(-10px);
    border-color: rgba(147, 112, 219, 0.8);
    box-shadow: 0 8px 20px rgba(147, 112, 219, 0.3);
  }

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(
      to bottom,
      rgba(26, 26, 46, 0.3),
      rgba(22, 33, 62, 0.9)
    );
    z-index: 1;
  }
`;

const CardBackground = styled(Box)`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;

  .gatsby-image-wrapper {
    height: 100%;
  }
`;

const CardContent = styled(VStack)`
  position: relative;
  z-index: 2;
  height: 100%;
  padding: 2rem;
  justify-content: flex-end;
  align-items: flex-start;
  color: white;
`;

const CardTitle = styled(Text)`
  font-size: 1.8rem;
  font-weight: bold;
  margin-bottom: 0.5rem;
`;

const CardDescription = styled(Text)`
  text-align: center;
  font-size: 1rem;
  opacity: 0.9;
`;

const HomePage = ({ data }) => {
  const { t } = useTranslation();
  
  const tierListSections = [
    {
      title: t('cards.compsTierList.title'),
      description: t('cards.compsTierList.description'),
      link: '/tierList/comps',
      image: data.championsBg
    },
    {
      title: t('cards.teamBuilder.title'),
      description: t('cards.teamBuilder.description'),
      link: '/builder/teamBuilder',
      image: data.builderBg1
    }
    // {
    //   title: 'Augments Tier List',
    //   description: 'Find the strongest augments ranked by performance and win rate.',
    //   link: '/tier-list/augments',
    //   image: data.augmentsBg
    // }
    
  ];
  
  const gameInfoSections = [
    {
      title: t('cards.champions.title'),
      description: t('cards.champions.description'),
      link: '/info/champions',
      image: data.championsBg2
    },
    {
      title: t('cards.traits.title'),
      description: t('cards.traits.description'),
      link: '/info/traits',
      image: data.traitsBg2
    },
    {
      title: t('cards.augments.title'),
      description: t('cards.augments.description'),
      link: '/info/augments',
      image: data.traitsBg
    },
    {
      title: t('cards.items.title'),
      description: t('cards.items.description'),
      link: '/info/items',
      image: data.itemsBg
    }
  ];


  const builderSections = [
    {
      title: 'Team Builder',
      description: 'Create and optimize your own team compositions with our interactive builder.',
      link: '/builder/teamBuilder',
      image: data.tier2Bg
    }
  ];

  return (
    <>
      <Seo 
        title={t('meta.title')} 
        description={t('meta.description')}
      />
      <Layout>
        <PageContainer>
          <ContentContainer>
            <PageTitle>{t('hero.title')}</PageTitle>
            <Slogan>{t('hero.slogan')}</Slogan>
            <Description>{t('hero.description')}</Description>
            
            <SectionTitle as="h2">{t('sections.tierList')}</SectionTitle>
            <SimpleGrid columns={{ base: 2, md: 2 }} spacing={6}>
              {tierListSections.map((section, index) => (
                <InfoCard key={index} to={section.link}>
                  <CardBackground>
                    <GatsbyImage
                      image={getImage(section.image)}
                      alt={section.title}
                      style={{ height: '100%' }}
                    />
                  </CardBackground>
                  <CardContent>
                    <CardTitle>{section.title}</CardTitle>
                    <CardDescription>{section.description}</CardDescription>
                  </CardContent>
                </InfoCard>
              ))}
            </SimpleGrid>

            <SectionTitle as="h2">{t('sections.gameInfo')}</SectionTitle>
            <SimpleGrid columns={{ base: 4}} spacing={6}>
              {gameInfoSections.map((section, index) => (
                <InfoCard key={index} to={section.link}>
                  <CardBackground>
                    <GatsbyImage
                      image={getImage(section.image)}
                      alt={section.title}
                      style={{ height: '100%' }}
                    />
                  </CardBackground>
                  <CardContent>
                    <CardTitle>{section.title}</CardTitle>
                    <CardDescription>{section.description}</CardDescription>
                  </CardContent>
                </InfoCard>
              ))}
            </SimpleGrid>

  

            {/* <SectionTitle as="h2">Team Builder</SectionTitle>
            <SimpleGrid columns={{ base: 1, md: 1}} spacing={6}>
              {builderSections.map((section, index) => (
                <InfoCard key={index} to={section.link}>
                  <CardBackground>
                    <GatsbyImage
                      image={getImage(section.image)}
                      alt={section.title}
                      style={{ height: '100%' }}
                    />
                  </CardBackground>
                  <CardContent>
                    <CardTitle>{section.title}</CardTitle>
                    <CardDescription>{section.description}</CardDescription>
                  </CardContent>
                </InfoCard>
              ))}
            </SimpleGrid> */}


          </ContentContainer>
          <Footer />
        </PageContainer>
      </Layout>
    </>
  );
};

export default HomePage;

// This is required for gatsby-plugin-react-i18next
export const query = graphql`
  query($language: String!) {
    locales: allLocale(filter: {language: {eq: $language}}) {
      edges {
        node {
          ns
          data
          language
        }
      }
    }
    augmentsBg: file(relativePath: { eq: "backgrounds/augments-bg.png" }) {
      childImageSharp {
        gatsbyImageData(
          width: 600
          formats: [AUTO, WEBP]
          placeholder: BLURRED
          layout: FULL_WIDTH
        )
      }
    }
    championsBg: file(relativePath: { eq: "backgrounds/champions-bg.png" }) {
      childImageSharp {
        gatsbyImageData(
          width: 600
          formats: [AUTO, WEBP]
          placeholder: BLURRED
          layout: FULL_WIDTH
        )
      }
    }
    championsBg2: file(relativePath: { eq: "backgrounds/champions-bg2.png" }) {
      childImageSharp {
        gatsbyImageData(
          width: 600
          formats: [AUTO, WEBP]
          placeholder: BLURRED
          layout: FULL_WIDTH
        )
      }
    }
    itemsBg: file(relativePath: { eq: "backgrounds/item2-bg.png" }) {
      childImageSharp {
        gatsbyImageData(
          width: 600
          formats: [AUTO, WEBP]
          placeholder: BLURRED
          layout: FULL_WIDTH
        )
      }
    }
    traitsBg: file(relativePath: { eq: "backgrounds/ball-bg.png" }) {
      childImageSharp {
        gatsbyImageData(
          width: 600
          formats: [AUTO, WEBP]
          placeholder: BLURRED
          layout: FULL_WIDTH
        )
      }
    }
    traitsBg2: file(relativePath: { eq: "backgrounds/traits-bg2.png" }) {
      childImageSharp {
        gatsbyImageData(
          width: 600
          formats: [AUTO, WEBP]
          placeholder: BLURRED
          layout: FULL_WIDTH
        )
      }
    }
    tier1Bg: file(relativePath: { eq: "backgrounds/tier1-bg.png" }) {
      childImageSharp {
        gatsbyImageData(
          width: 600
          formats: [AUTO, WEBP]
          placeholder: BLURRED
          layout: FULL_WIDTH
        )
      }
    }
    tier2Bg: file(relativePath: { eq: "backgrounds/tier2-bg.png" }) {
      childImageSharp {
        gatsbyImageData(
          width: 600
          formats: [AUTO, WEBP]
          placeholder: BLURRED
          layout: FULL_WIDTH
        )
      }
    }
    builderBg1: file(relativePath: { eq: "backgrounds/builder-bg1.png" }) {
      childImageSharp {
        gatsbyImageData(
          width: 700
          formats: [AUTO, WEBP]
          placeholder: BLURRED
          layout: FULL_WIDTH
        )
      }
    }
  }
`; 